/**
 * Popperverse Solana Program Utilities
 * 
 * This module provides helper functions and constants for interacting with
 * the Popperverse Solana program from the frontend.
 * 
 * All transaction pages (Checkout, Vote, Create, Donate) should use these
 * utilities to call the actual on-chain program instructions.
 */

import { PublicKey, Transaction, TransactionInstruction } from '@solana/web3.js';
import { Buffer } from 'buffer';
import bs58 from 'bs58';
import {
  DISCRIMINATORS,
  POPPER_NFT_LAYOUT,
  VOTING_ROUND_LAYOUT,
  PDA_SEEDS,
} from '../constants/accountLayouts';

// ============================================
// RPC CALL CACHE (prevents rate-limiting bursts)
// ============================================
const _rpcCache = new Map();
const _inflightRequests = new Map(); // Deduplicates concurrent identical requests
const RPC_CACHE_TTL = 60_000; // 60 seconds

function getCached(key) {
  const entry = _rpcCache.get(key);
  if (entry && Date.now() - entry.ts < RPC_CACHE_TTL) return entry.data;
  _rpcCache.delete(key);
  return null;
}

function setCache(key, data) {
  _rpcCache.set(key, { data, ts: Date.now() });
}

/**
 * Deduplicate concurrent calls - if a request with the same key is already
 * in-flight, return the same promise instead of firing a new RPC call.
 */
function deduplicatedFetch(key, fetchFn) {
  // Check cache first
  const cached = getCached(key);
  if (cached) return Promise.resolve(cached);
  
  // Check if this exact request is already in-flight
  if (_inflightRequests.has(key)) {
    console.log(`[RPC] Deduplicating in-flight request: ${key}`);
    return _inflightRequests.get(key);
  }
  
  // Start new request and track it
  const promise = fetchFn()
    .then(result => {
      setCache(key, result);
      _inflightRequests.delete(key);
      return result;
    })
    .catch(err => {
      _inflightRequests.delete(key);
      throw err;
    });
  
  _inflightRequests.set(key, promise);
  return promise;
}

/** Clear the RPC cache (call after mutations) */
export function clearRpcCache() {
  _rpcCache.clear();
}

// ============================================
// PROGRAM & WALLET CONSTANTS
// ============================================
// Popperverse Program ID (deployed on devnet/mainnet)
export const PROGRAM_ID = new PublicKey(
  process.env.REACT_APP_PROGRAM_ID || '5TCHJUEFkrPSAk4JWrANEdTXcLs6JVMSpe3ZrHocFW51'
);

// SEEKER Wallet addresses (cold storage - final destinations for non-swap flows)
// These are used for direct transfers that don't require Jupiter swaps
export const SEEKER_WALLETS = {
  TREASURY: new PublicKey('2Rvwr4ALqvJG3NijdeFveMEpJUDQttTwQT2iFQDKBmQV'),
  DONATE: new PublicKey('5KZecTWxjM2dZAoj5tQwDbJYJbwiaGaXbA4UsfYHszU3'),
  VOTE: new PublicKey('ENpmUmmK1GNLSmSSXD4noYAVS7xSSCV7e6FXcaR2kbNE'),
  CREATE: new PublicKey('8UYGd6mP7uVuwyESuhsg1GQKjzJix7uyU9xySbcXBdCD'),
  TAX: new PublicKey('G3JwssMQf5rdTJTwrLUWjPt18tAmyDP8jGEMzAvdW5R3'),
};

// SOLFLARE Wallet addresses (hot wallets - for automation/swap intermediaries)
// Only used for flows that require swaps via Jupiter
export const SOLFLARE_WALLETS = {
  TREASURY: new PublicKey('5tRtebqx7emMKK5rr3H2JuzBUbrSgi6B7PuAP66rf9Tv'),
  DONATE: new PublicKey('2DM26isCsYWUzwfRu2DQht88ysxfVD78M3JM4PDyude8'),
  VOTE: new PublicKey('FWJWt8A2jeeLYo7cVBEupuJM2FZ9HW7XzRbeSxtnG3es'),
  CREATE: new PublicKey('SRjdiqJ8SthmQp3FFqWRXh4hRjPxuFFF6nrj9fr3da8'),
  TAX: new PublicKey('G3JwssMQf5rdTJTwrLUWjPt18tAmyDP8jGEMzAvdW5R3'),
};

// Default wallet exports - use SOLFLARE for on-chain instructions
// The on-chain program validates against SOLFLARE_* addresses
export const WALLETS = {
  TREASURY: SOLFLARE_WALLETS.TREASURY,  // On-chain validates SOLFLARE_TREASURY (for automation)
  DONATE: SEEKER_WALLETS.DONATE,        // Direct to cold storage
  VOTE: SOLFLARE_WALLETS.VOTE,          // On-chain validates SOLFLARE_VOTE (until program redeployed)
  CREATE: SEEKER_WALLETS.CREATE,        // Direct to cold storage
  DEFAULT_CREATOR: new PublicKey(process.env.REACT_APP_DEFAULT_CREATOR_WALLET || '2n2wbmzxsgFgVutwMxB79zRsZ5UT2K82yyYk9KYVLpUq'),
};

// Program Authority - the wallet that can perform admin functions
export const PROGRAM_AUTHORITY_WALLET = new PublicKey(
  process.env.REACT_APP_PROGRAM_AUTHORITY || 'DM5ZyneSNXYZ6eHFKfyXGSMe1jc6RTERnVHN3J8weNvU'
);

// System Program ID
export const SYSTEM_PROGRAM_ID = new PublicKey('11111111111111111111111111111111');

// ============================================
// PDA DERIVATION HELPERS
// ============================================

/**
 * Derive the Tax Escrow PDA
 */
export async function getTaxEscrowPDA() {
  const [pda] = await PublicKey.findProgramAddress(
    [Buffer.from('tax_escrow')],
    PROGRAM_ID
  );
  return pda;
}

/**
 * Derive the POPCAT Escrow PDA
 */
export async function getPopcatEscrowPDA() {
  const [pda] = await PublicKey.findProgramAddress(
    [Buffer.from('popcat_escrow')],
    PROGRAM_ID
  );
  return pda;
}

/**
 * Derive the POPPERS Escrow PDA
 */
export async function getPoppersEscrowPDA() {
  const [pda] = await PublicKey.findProgramAddress(
    [Buffer.from('poppers_escrow')],
    PROGRAM_ID
  );
  return pda;
}

/**
 * Get collection accounts (mint, metadata, master edition) from environment
 * 
 * @returns {Object} Collection accounts
 * @returns {PublicKey} collectionMint - Collection NFT mint address
 * @returns {PublicKey} collectionMetadata - Collection metadata PDA
 * @returns {PublicKey} collectionMasterEdition - Collection master edition PDA
 * @throws {Error} If REACT_APP_COLLECTION_MINT not set or invalid
 */
export function getCollectionAccounts() {
  const collectionMintStr = process.env.REACT_APP_COLLECTION_MINT;
  
  if (!collectionMintStr) {
    throw new Error('REACT_APP_COLLECTION_MINT not set in environment. Run deployment script to create collection NFT.');
  }
  
  const collectionMint = new PublicKey(collectionMintStr);
  
  // Derive metadata PDA: ["metadata", metaplex_program, mint]
  const [collectionMetadata] = PublicKey.findProgramAddressSync(
    [
      Buffer.from('metadata'),
      TOKEN_METADATA_PROGRAM_ID.toBuffer(),
      collectionMint.toBuffer(),
    ],
    TOKEN_METADATA_PROGRAM_ID
  );
  
  // Derive master edition PDA: ["metadata", metaplex_program, mint, "edition"]
  const [collectionMasterEdition] = PublicKey.findProgramAddressSync(
    [
      Buffer.from('metadata'),
      TOKEN_METADATA_PROGRAM_ID.toBuffer(),
      collectionMint.toBuffer(),
      Buffer.from('edition'),
    ],
    TOKEN_METADATA_PROGRAM_ID
  );
  
  return {
    collectionMint,
    collectionMetadata,
    collectionMasterEdition,
  };
}

// ============================================
// INSTRUCTION DISCRIMINATORS
// ============================================
// These are the 8-byte discriminators from the centralized constants file

const PURCHASE_DISCRIMINATOR = Buffer.from(DISCRIMINATORS.INSTRUCTIONS.PURCHASE);
const VOTE_DISCRIMINATOR = Buffer.from(DISCRIMINATORS.INSTRUCTIONS.VOTE_FOR_NFT);
const VOTE_WITH_STATS_DISCRIMINATOR = Buffer.from(DISCRIMINATORS.INSTRUCTIONS.VOTE_FOR_NFT_WITH_STATS);
const CREATE_DESIGN_DISCRIMINATOR = Buffer.from(DISCRIMINATORS.INSTRUCTIONS.CREATE_DESIGN);
const DONATE_DISCRIMINATOR = Buffer.from(DISCRIMINATORS.INSTRUCTIONS.DONATE);
const MINT_POPPER_NFT_DISCRIMINATOR = Buffer.from(DISCRIMINATORS.INSTRUCTIONS.MINT_POPPER_NFT);

// Metaplex Token Metadata Program
export const TOKEN_METADATA_PROGRAM_ID = new PublicKey('metaqbxxUerdq28cj1RbAWkYQm3ybzjb6a8bt518x1s');

// SPL Token Program
export const TOKEN_PROGRAM_ID = new PublicKey('TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA');

// Associated Token Program
export const ASSOCIATED_TOKEN_PROGRAM_ID = new PublicKey('ATokenGPvbdGVxr1b2hvZbsiqW5xWH25efTNsLJA8knL');

// Rent Sysvar
export const RENT_SYSVAR_ID = new PublicKey('SysvarRent111111111111111111111111111111111');

// ============================================
// BORSH SERIALIZATION HELPERS
// ============================================

/**
 * Encode a string for Borsh serialization (length-prefixed)
 */
function encodeString(str) {
  const strBytes = Buffer.from(str, 'utf8');
  const lenBytes = Buffer.alloc(4);
  lenBytes.writeUInt32LE(strBytes.length, 0);
  return Buffer.concat([lenBytes, strBytes]);
}

/**
 * Encode a u64 for Borsh serialization
 */
function encodeU64(value) {
  const buf = Buffer.alloc(8);
  // eslint-disable-next-line no-undef
  buf.writeBigUInt64LE(BigInt(value), 0);
  return buf;
}

/**
 * Encode a u32 for Borsh serialization
 */
function encodeU32(value) {
  const buf = Buffer.alloc(4);
  buf.writeUInt32LE(value, 0);
  return buf;
}

/**
 * Encode an optional string for Borsh (Option<String>)
 */
function encodeOptionString(str) {
  if (str === null || str === undefined) {
    return Buffer.from([0]); // None
  }
  return Buffer.concat([Buffer.from([1]), encodeString(str)]); // Some
}

// ============================================
// INSTRUCTION BUILDERS
// ============================================

/**
 * Build the Purchase instruction
 * 
 * @param {Object} params
 * @param {PublicKey} params.buyer - Buyer's wallet
 * @param {PublicKey} params.nftMint - NFT mint address (used to derive PopperNFT PDA)
 * @param {PublicKey} params.creatorWallet - Original NFT creator receiving 1% royalty (permanent)
 * @param {PublicKey} params.nftHolderWallet - Current NFT holder receiving 4% royalty (transferable)
 * @param {PublicKey} params.seekerTreasury - Treasury wallet
 * @param {PublicKey} params.seekerDonate - Donation wallet
 * @param {PublicKey} params.taxEscrow - Tax escrow PDA
 * @param {PublicKey} params.popcatEscrow - POPCAT escrow PDA
 * @param {PublicKey} params.poppersEscrow - POPPERS escrow PDA
 * @param {string} params.orderId - Order ID string
 * @param {string} params.productCode - Product code string
 * @param {number} params.subtotalLamports - Subtotal in lamports
 * @param {number} params.taxLamports - Tax in lamports
 * @param {number} params.royaltyLamports - Total royalty in lamports (5%, split 1%+4%)
 * @param {number} params.donationLamports - Donation in lamports
 */
export function buildPurchaseInstruction({
  buyer,
  nftMint,
  creatorWallet,
  nftHolderWallet,
  seekerTreasury,
  seekerDonate,
  taxEscrow,
  popcatEscrow,
  poppersEscrow,
  orderId,
  productCode,
  subtotalLamports,
  taxLamports,
  royaltyLamports,
  donationLamports,
}) {
  // Derive PopperNFT PDA from nft_mint
  const [popperNftPda] = PublicKey.findProgramAddressSync(
    [Buffer.from('popper_nft'), nftMint.toBuffer()],
    PROGRAM_ID
  );

  // Serialize instruction data
  const data = Buffer.concat([
    PURCHASE_DISCRIMINATOR,
    encodeString(orderId),
    encodeString(productCode),
    encodeU64(subtotalLamports),
    encodeU64(taxLamports),
    encodeU64(royaltyLamports),
    encodeU64(donationLamports),
  ]);

  // Build account keys in order from IDL:
  // buyer, nft_mint, popper_nft, creator_wallet, nft_holder_wallet, 
  // seeker_treasury, seeker_donate, tax_escrow, popcat_escrow, poppers_escrow, system_program
  const keys = [
    { pubkey: buyer, isSigner: true, isWritable: true },              // buyer
    { pubkey: nftMint, isSigner: false, isWritable: false },          // nft_mint
    { pubkey: popperNftPda, isSigner: false, isWritable: false },     // popper_nft (PDA)
    { pubkey: creatorWallet, isSigner: false, isWritable: true },     // creator_wallet (1% royalty)
    { pubkey: nftHolderWallet, isSigner: false, isWritable: true },   // nft_holder_wallet (4% royalty)
    { pubkey: seekerTreasury, isSigner: false, isWritable: true },    // seeker_treasury
    { pubkey: seekerDonate, isSigner: false, isWritable: true },      // seeker_donate
    { pubkey: taxEscrow, isSigner: false, isWritable: true },         // tax_escrow
    { pubkey: popcatEscrow, isSigner: false, isWritable: true },      // popcat_escrow
    { pubkey: poppersEscrow, isSigner: false, isWritable: true },     // poppers_escrow
    { pubkey: SYSTEM_PROGRAM_ID, isSigner: false, isWritable: false }, // system_program
  ];

  return new TransactionInstruction({
    keys,
    programId: PROGRAM_ID,
    data,
  });
}

/**
 * Build the Vote for NFT instruction
 * 
 * @param {Object} params
 * @param {PublicKey} params.voter - Voter's wallet
 * @param {PublicKey} params.nftMint - The NFT mint being voted for
 * @param {PublicKey} params.voteWallet - Vote collection wallet
 * @param {number} params.roundNumber - Voting round number (u8)
 * @param {number} params.amountLamports - Vote amount in lamports
 */
export function buildVoteInstruction({
  voter,
  nftMint,
  voteWallet,
  roundNumber,
  amountLamports,
}) {
  // Derive PopperNFT PDA
  const [popperNftPda] = PublicKey.findProgramAddressSync(
    [Buffer.from('popper_nft'), nftMint.toBuffer()],
    PROGRAM_ID
  );
  
  // Derive VoteRecord PDA
  const [voteRecordPda] = PublicKey.findProgramAddressSync(
    [Buffer.from('nft_vote'), nftMint.toBuffer(), voter.toBuffer()],
    PROGRAM_ID
  );
  
  // Derive VotingRound PDA
  const [votingRoundPda] = PublicKey.findProgramAddressSync(
    [Buffer.from('voting_round'), Buffer.from([roundNumber])],
    PROGRAM_ID
  );

  // Serialize instruction data: amount_lamports (u64) + round_number (u8)
  const data = Buffer.concat([
    VOTE_DISCRIMINATOR,
    encodeU64(amountLamports),
    Buffer.from([roundNumber]),  // u8 for round_number
  ]);

  // Build account keys in order from IDL
  const keys = [
    { pubkey: voter, isSigner: true, isWritable: true },           // voter
    { pubkey: nftMint, isSigner: false, isWritable: false },       // nft_mint
    { pubkey: popperNftPda, isSigner: false, isWritable: true },   // popper_nft
    { pubkey: voteRecordPda, isSigner: false, isWritable: true },  // vote_record
    { pubkey: votingRoundPda, isSigner: false, isWritable: true }, // voting_round_account
    { pubkey: voteWallet, isSigner: false, isWritable: true },     // vote_wallet
    { pubkey: SYSTEM_PROGRAM_ID, isSigner: false, isWritable: false }, // system_program
  ];

  return new TransactionInstruction({
    keys,
    programId: PROGRAM_ID,
    data,
  });
}

/**
 * Build the Vote with Stats instruction (vote_for_nft_with_stats)
 * 
 * This allows voters to submit ratings along with their vote.
 * Ratings are 1-10 for each category: Cuteness, Creativity, Pop Power, Originality, Potential
 * 
 * @param {Object} params
 * @param {PublicKey} params.voter - Voter's wallet
 * @param {PublicKey} params.nftMint - The NFT mint being voted for
 * @param {PublicKey} params.voteWallet - Vote collection wallet
 * @param {number} params.roundNumber - Voting round number (u8)
 * @param {number} params.amountLamports - Vote amount in lamports
 * @param {number[]} params.ratings - Array of 5 ratings [cuteness, creativity, popPower, originality, potential], each 1-10
 */
export function buildVoteWithStatsInstruction({
  voter,
  nftMint,
  voteWallet,
  roundNumber,
  amountLamports,
  ratings,
}) {
  // Validate ratings
  if (!ratings || ratings.length !== 5) {
    throw new Error('Ratings must be an array of 5 values');
  }
  for (const r of ratings) {
    if (r < 1 || r > 10) {
      throw new Error('Each rating must be between 1 and 10');
    }
  }
  
  // Derive PopperNFT PDA
  const [popperNftPda] = PublicKey.findProgramAddressSync(
    [Buffer.from('popper_nft'), nftMint.toBuffer()],
    PROGRAM_ID
  );
  
  // Derive VoteRecord PDA
  const [voteRecordPda] = PublicKey.findProgramAddressSync(
    [Buffer.from('nft_vote'), nftMint.toBuffer(), voter.toBuffer()],
    PROGRAM_ID
  );
  
  // Derive VotingRound PDA
  const [votingRoundPda] = PublicKey.findProgramAddressSync(
    [Buffer.from('voting_round'), Buffer.from([roundNumber])],
    PROGRAM_ID
  );

  // Serialize instruction data: amount_lamports (u64) + round_number (u8) + ratings ([u8; 5])
  const data = Buffer.concat([
    VOTE_WITH_STATS_DISCRIMINATOR,
    encodeU64(amountLamports),
    Buffer.from([roundNumber]),  // u8 for round_number
    Buffer.from(ratings),        // [u8; 5] for ratings
  ]);

  // Build account keys in order from IDL (same as vote_for_nft)
  const keys = [
    { pubkey: voter, isSigner: true, isWritable: true },           // voter
    { pubkey: nftMint, isSigner: false, isWritable: false },       // nft_mint
    { pubkey: popperNftPda, isSigner: false, isWritable: true },   // popper_nft
    { pubkey: voteRecordPda, isSigner: false, isWritable: true },  // vote_record
    { pubkey: votingRoundPda, isSigner: false, isWritable: true }, // voting_round_account
    { pubkey: voteWallet, isSigner: false, isWritable: true },     // vote_wallet
    { pubkey: SYSTEM_PROGRAM_ID, isSigner: false, isWritable: false }, // system_program
  ];

  return new TransactionInstruction({
    keys,
    programId: PROGRAM_ID,
    data,
  });
}

/**
 * Build the Create Design instruction
 * 
 * @param {Object} params
 * @param {PublicKey} params.creator - Creator's wallet
 * @param {PublicKey} params.createWallet - Create fee collection wallet
 * @param {string} params.designName - Name of the design
 * @param {number} params.productNumber - Product number (u32)
 * @param {number} params.amountLamports - Payment amount in lamports
 */
export function buildCreateDesignInstruction({
  creator,
  createWallet,
  designName,
  productNumber,
  amountLamports,
}) {
  // Serialize instruction data
  const data = Buffer.concat([
    CREATE_DESIGN_DISCRIMINATOR,
    encodeString(designName),
    encodeU32(productNumber),
    encodeU64(amountLamports),
  ]);

  // Build account keys in order from IDL
  const keys = [
    { pubkey: creator, isSigner: true, isWritable: true },      // creator
    { pubkey: createWallet, isSigner: false, isWritable: true }, // create_wallet
    { pubkey: SYSTEM_PROGRAM_ID, isSigner: false, isWritable: false }, // system_program
  ];

  return new TransactionInstruction({
    keys,
    programId: PROGRAM_ID,
    data,
  });
}

/**
 * Build the Donate instruction
 * 
 * @param {Object} params
 * @param {PublicKey} params.donor - Donor's wallet
 * @param {PublicKey} params.donateWallet - Donation collection wallet
 * @param {number} params.amountLamports - Donation amount in lamports
 * @param {string|null} params.message - Optional donation message
 */
export function buildDonateInstruction({
  donor,
  donateWallet,
  amountLamports,
  message = null,
}) {
  // Serialize instruction data
  const data = Buffer.concat([
    DONATE_DISCRIMINATOR,
    encodeU64(amountLamports),
    encodeOptionString(message),
  ]);

  // Build account keys in order from IDL
  const keys = [
    { pubkey: donor, isSigner: true, isWritable: true },       // donor
    { pubkey: donateWallet, isSigner: false, isWritable: true }, // donate_wallet
    { pubkey: SYSTEM_PROGRAM_ID, isSigner: false, isWritable: false }, // system_program
  ];

  return new TransactionInstruction({
    keys,
    programId: PROGRAM_ID,
    data,
  });
}

// ============================================
// TRANSACTION HELPERS
// ============================================

/**
 * Create a transaction with the given instruction
 */
export function createTransaction(instruction) {
  const tx = new Transaction();
  tx.add(instruction);
  return tx;
}

/**
 * Generate a unique order ID
 */
export function generateOrderId() {
  const date = new Date();
  const dateStr = date.toISOString().slice(0, 10).replace(/-/g, '');
  const random = Math.floor(Math.random() * 100000).toString().padStart(5, '0');
  return `ORD-${dateStr}-${random}`;
}

// ============================================
// NFT MINTING HELPERS
// ============================================

/**
 * Derive the PopperNFT PDA for a given mint
 */
export async function getPopperNFTPDA(nftMint) {
  const [pda] = await PublicKey.findProgramAddress(
    [Buffer.from('popper_nft'), nftMint.toBuffer()],
    PROGRAM_ID
  );
  return pda;
}

/**
 * Derive the VotingRound PDA for a given round number
 */
export async function getVotingRoundPDA(roundNumber) {
  const [pda] = await PublicKey.findProgramAddress(
    [Buffer.from('voting_round'), Buffer.from([roundNumber])],
    PROGRAM_ID
  );
  return pda;
}

/**
 * Fetch and decode VotingRound account data from the blockchain
 * 
 * @param {Connection} connection - Solana connection
 * @param {number} roundNumber - The round number to fetch
 * @returns {Object|null} Decoded VotingRound data or null if not found
 * 
 * VotingRound struct layout (116 bytes total):
 * - 8 bytes: discriminator
 * - 1 byte: round_number (u8)
 * - 8 bytes: start_timestamp (i64)
 * - 8 bytes: end_timestamp (i64)
 * - 1 + 32 bytes: winner_mint (Option<Pubkey>)
 * - 8 bytes: total_nfts_created (u64)
 * - 8 bytes: total_votes_cast (u64)
 * - 8 bytes: total_sol_voted (u64)
 * - 8 bytes: total_poppers_voted (u64)
 * - 1 byte: poppers_swap_enabled (bool)
 * - 1 byte: is_active (bool)
 * - 32 bytes: authority (Pubkey)
 * - 1 byte: bump (u8)
 */
export async function fetchVotingRound(connection, roundNumber) {
  try {
    const pda = await getVotingRoundPDA(roundNumber);
    const accountInfo = await connection.getAccountInfo(pda);
    
    if (!accountInfo || !accountInfo.data) {
      console.log(`VotingRound ${roundNumber} not found on-chain`);
      return null;
    }
    
    const data = accountInfo.data;
    
    // Skip 8-byte discriminator
    let offset = 8;
    
    // round_number (u8)
    const round_number = data.readUInt8(offset);
    offset += 1;
    
    // start_timestamp (i64) - read as BigInt then convert to Number
    const start_timestamp = Number(data.readBigInt64LE(offset));
    offset += 8;
    
    // end_timestamp (i64)
    const end_timestamp = Number(data.readBigInt64LE(offset));
    offset += 8;
    
    // winner_mint (Option<Pubkey>) - 1 byte flag + 32 bytes if present
    const hasWinner = data.readUInt8(offset) === 1;
    offset += 1;
    let winner_mint = null;
    if (hasWinner) {
      winner_mint = new PublicKey(data.slice(offset, offset + 32));
    }
    offset += 32;
    
    // total_nfts_created (u64)
    const total_nfts_created = Number(data.readBigUInt64LE(offset));
    offset += 8;
    
    // total_votes_cast (u64)
    const total_votes_cast = Number(data.readBigUInt64LE(offset));
    offset += 8;
    
    // total_sol_voted (u64)
    const total_sol_voted = Number(data.readBigUInt64LE(offset));
    offset += 8;
    
    // total_poppers_voted (u64)
    const total_poppers_voted = Number(data.readBigUInt64LE(offset));
    offset += 8;
    
    // poppers_swap_enabled (bool)
    const poppers_swap_enabled = data.readUInt8(offset) === 1;
    offset += 1;
    
    // is_active (bool)
    const is_active = data.readUInt8(offset) === 1;
    offset += 1;
    
    // authority (Pubkey)
    const authority = new PublicKey(data.slice(offset, offset + 32));
    offset += 32;
    
    // bump (u8)
    const bump = data.readUInt8(offset);
    
    return {
      pda: pda.toBase58(),
      round_number,
      start_timestamp,
      end_timestamp,
      winner_mint: winner_mint?.toBase58() || null,
      total_nfts_created,
      total_votes_cast,
      total_sol_voted,
      total_poppers_voted,
      poppers_swap_enabled,
      is_active,
      authority: authority.toBase58(),
      bump,
    };
  } catch (error) {
    console.error('Error fetching VotingRound:', error);
    return null;
  }
}

/**
 * Derive the Metadata Account PDA for an NFT
 */
export async function getMetadataAccountPDA(nftMint) {
  const [pda] = await PublicKey.findProgramAddress(
    [
      Buffer.from('metadata'),
      TOKEN_METADATA_PROGRAM_ID.toBuffer(),
      nftMint.toBuffer()
    ],
    TOKEN_METADATA_PROGRAM_ID
  );
  return pda;
}

/**
 * Derive the Master Edition PDA for an NFT
 */
export async function getMasterEditionPDA(nftMint) {
  const [pda] = await PublicKey.findProgramAddress(
    [
      Buffer.from('metadata'),
      TOKEN_METADATA_PROGRAM_ID.toBuffer(),
      nftMint.toBuffer(),
      Buffer.from('edition')
    ],
    TOKEN_METADATA_PROGRAM_ID
  );
  return pda;
}

/**
 * Derive the Associated Token Account for a wallet and mint
 */
export async function getAssociatedTokenAddress(wallet, mint) {
  const [pda] = await PublicKey.findProgramAddress(
    [
      wallet.toBuffer(),
      TOKEN_PROGRAM_ID.toBuffer(),
      mint.toBuffer()
    ],
    ASSOCIATED_TOKEN_PROGRAM_ID
  );
  return pda;
}

/**
 * Build the MintPopperNFT instruction
 * 
 * @param {Object} params
 * @param {PublicKey} params.creator - Creator's wallet (signer)
 * @param {PublicKey} params.nftMint - New mint account (signer, must be created externally)
 * @param {PublicKey} params.tokenAccount - Creator's ATA for the NFT
 * @param {PublicKey} params.metadataAccount - Metaplex metadata PDA
 * @param {PublicKey} params.masterEdition - Metaplex master edition PDA
 * @param {PublicKey} params.popperNft - PopperNFT PDA
 * @param {PublicKey} params.votingRoundAccount - VotingRound PDA
 * @param {PublicKey} params.createWallet - Wallet receiving creation fee
 * @param {string} params.name - NFT name (3-32 chars)
 * @param {string} params.symbol - NFT symbol (2-10 chars)
 * @param {string} params.uri - Metadata URI (Arweave URL)
 * @param {number} params.creationFeeLamports - Fee in lamports
 */
export function buildMintPopperNFTInstruction({
  payer,
  creatorWallet,
  nftMint,
  tokenAccount,
  metadataAccount,
  masterEdition,
  popperNft,
  votingRoundAccount,
  createWallet,
  collectionMint,
  collectionMetadata,
  collectionMasterEdition,
  collectionAuthority,
  name,
  symbol,
  uri,
  creationFeeLamports,
}) {
  // Serialize instruction data
  const data = Buffer.concat([
    MINT_POPPER_NFT_DISCRIMINATOR,
    encodeString(name),
    encodeString(symbol),
    encodeString(uri),
    encodeU64(creationFeeLamports),
  ]);

  // Build account keys in order from IDL
  const keys = [
    { pubkey: payer, isSigner: true, isWritable: true },
    { pubkey: creatorWallet, isSigner: false, isWritable: false },
    { pubkey: nftMint, isSigner: true, isWritable: true },
    { pubkey: tokenAccount, isSigner: false, isWritable: true },
    { pubkey: metadataAccount, isSigner: false, isWritable: true },
    { pubkey: masterEdition, isSigner: false, isWritable: true },
    { pubkey: popperNft, isSigner: false, isWritable: true },
    { pubkey: votingRoundAccount, isSigner: false, isWritable: true },
    { pubkey: createWallet, isSigner: false, isWritable: true },
    { pubkey: collectionMint, isSigner: false, isWritable: false },
    { pubkey: collectionMetadata, isSigner: false, isWritable: true },
    { pubkey: collectionMasterEdition, isSigner: false, isWritable: false },
    { pubkey: collectionAuthority, isSigner: true, isWritable: false },
    { pubkey: TOKEN_METADATA_PROGRAM_ID, isSigner: false, isWritable: false },
    { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
    { pubkey: ASSOCIATED_TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
    { pubkey: SYSTEM_PROGRAM_ID, isSigner: false, isWritable: false },
    { pubkey: RENT_SYSVAR_ID, isSigner: false, isWritable: false },
  ];

  return new TransactionInstruction({
    keys,
    programId: PROGRAM_ID,
    data,
  });
}

/**
 * Build set_product_image instruction (admin only)
 * Sets the product photo URL for a winning NFT
 * 
 * @param {Object} params
 * @param {PublicKey} params.authority - Admin wallet
 * @param {PublicKey} params.nftMint - NFT mint address
 * @param {string} params.productImageUri - Product image URL (max 100 chars)
 * @returns {TransactionInstruction} Transaction instruction
 */
export function buildSetProductImageInstruction({
  authority,
  nftMint,
  productImageUri,
}) {
  // Get set_product_image discriminator from IDL: [93, 232, 202, 90, 159, 75, 96, 240]
  const SET_PRODUCT_IMAGE_DISCRIMINATOR = Buffer.from([93, 232, 202, 90, 159, 75, 96, 240]);
  
  // Derive program_authority PDA (required for admin functions)
  const PROGRAM_AUTHORITY_SEED = Buffer.from('program_authority');
  const [programAuthorityPda] = PublicKey.findProgramAddressSync(
    [PROGRAM_AUTHORITY_SEED],
    PROGRAM_ID
  );
  
  // Derive PopperNFT PDA
  const [popperNftPda] = PublicKey.findProgramAddressSync(
    [Buffer.from('popper_nft'), nftMint.toBuffer()],
    PROGRAM_ID
  );
  
  // Encode product_image_uri as string
  const data = Buffer.concat([
    SET_PRODUCT_IMAGE_DISCRIMINATOR,
    encodeString(productImageUri)
  ]);
  
  // Accounts must match IDL order: program_authority, authority, nft_mint, popper_nft
  const keys = [
    { pubkey: programAuthorityPda, isSigner: false, isWritable: false },
    { pubkey: authority, isSigner: true, isWritable: true },
    { pubkey: nftMint, isSigner: false, isWritable: false },
    { pubkey: popperNftPda, isSigner: false, isWritable: true },
  ];
  
  return new TransactionInstruction({
    keys,
    programId: PROGRAM_ID,
    data,
  });
}

/**
 * Build force_archive_nft instruction (admin only)
 * Archives an NFT immediately for policy violations
 * 
 * @param {Object} params
 * @param {PublicKey} params.authority - Admin wallet (program authority)
 * @param {PublicKey} params.nftMint - NFT mint to archive
 * @returns {TransactionInstruction}
 */
export function buildForceArchiveNFTInstruction({
  authority,
  nftMint,
}) {
  // Get force_archive_nft discriminator from IDL
  const FORCE_ARCHIVE_NFT_DISCRIMINATOR = Buffer.from([196, 40, 155, 123, 67, 196, 240, 185]);
  
  // Derive PDAs
  const PROGRAM_AUTHORITY_SEED = Buffer.from('program_authority');
  const [programAuthorityPda] = PublicKey.findProgramAddressSync(
    [PROGRAM_AUTHORITY_SEED],
    PROGRAM_ID
  );
  
  const [popperNftPda] = PublicKey.findProgramAddressSync(
    [Buffer.from('popper_nft'), nftMint.toBuffer()],
    PROGRAM_ID
  );
  
  const keys = [
    { pubkey: programAuthorityPda, isSigner: false, isWritable: false },
    { pubkey: authority, isSigner: true, isWritable: true },
    { pubkey: nftMint, isSigner: false, isWritable: false },
    { pubkey: popperNftPda, isSigner: false, isWritable: true },
  ];
  
  return new TransactionInstruction({
    keys,
    programId: PROGRAM_ID,
    data: FORCE_ARCHIVE_NFT_DISCRIMINATOR,
  });
}

// ============================================
// POPPER NFT FETCHING
// ============================================

// Use centralized discriminator from accountLayouts.js
const POPPER_NFT_DISCRIMINATOR = DISCRIMINATORS.POPPER_NFT;

/**
 * Fetch all PopperNFT accounts from the blockchain
 * 
 * @param {Connection} connection - Solana connection
 * @param {Object} options - Filter options
 * @param {boolean} options.votingEligibleOnly - Only return voting-eligible NFTs (default: true)
 * @param {number} options.roundNumber - Filter by created_in_round (optional)
 * @returns {Array} Array of PopperNFT data with decoded fields
 * 
 * PopperNFT struct layout (248 bytes total):
 * - 8 bytes: discriminator
 * - 32 bytes: creator_wallet (Pubkey)
 * - 32 bytes: current_owner (Pubkey)
 * - 32 bytes: nft_mint (Pubkey)
 * - 8 bytes: total_votes (u64)
 * - 8 bytes: total_vote_sol (u64)
 * - 8 bytes: total_vote_poppers (u64)
 * - 64 bytes: votes_per_round ([u64; 8])
 * - 64 bytes: sol_per_round ([u64; 8])
 * - 64 bytes: poppers_per_round ([u64; 8])
 * - 8 bytes: total_sales (u64)
 * - 8 bytes: total_royalties_paid (u64)
 * - 8 bytes: created_at (i64)
 * - 1 byte: created_in_round (u8)
 * - 1 byte: last_active_round (u8)
 * - 1 byte: is_winner (bool)
 * - 1 byte: is_archived (bool)
 * - 1 byte: is_voting_eligible (bool)
 * - 4 bytes: product_id (u32)
 * - 1 byte: bump (u8)
 */
export async function fetchAllPopperNFTs(connection, options = {}) {
  const { votingEligibleOnly = true, roundNumber } = options;
  
  // Use deduplication wrapper - concurrent callers share the same in-flight request
  const cacheKey = `popperNFTs_${votingEligibleOnly}_${roundNumber ?? 'all'}`;
  
  return deduplicatedFetch(cacheKey, async () => {
  try {
    // Get all accounts owned by the program with the PopperNFT discriminator
    const accounts = await connection.getProgramAccounts(PROGRAM_ID, {
      filters: [
        {
          memcmp: {
            offset: 0,
            bytes: bs58.encode(Buffer.from(POPPER_NFT_DISCRIMINATOR)),
          },
        },
      ],
    });
    
    console.log(`[fetchAllPopperNFTs] Found ${accounts.length} PopperNFT accounts`);
    
    const popperNfts = [];
    
    for (const { pubkey, account } of accounts) {
      try {
        const data = account.data;
        
        // Check data length
        // PopperNFT::LEN = 483 bytes (includes 8-byte discriminator)
        const expectedLen = 483;
        if (data.length < expectedLen) {
          console.warn(`[fetchAllPopperNFTs] Account ${pubkey.toBase58()} has old format: ${data.length} bytes (expected ${expectedLen}, skipping)`);
          continue;
        }
        
        // Skip 8-byte discriminator
        let offset = 8;
      
      // version (u8) - NEW FIELD
      const version = data.readUInt8(offset);
      offset += 1;
      
      // creator_wallet (32 bytes)
      const creatorWallet = new PublicKey(data.slice(offset, offset + 32));
      offset += 32;
      
      // current_owner (32 bytes)
      const currentOwner = new PublicKey(data.slice(offset, offset + 32));
      offset += 32;
      
      // nft_mint (32 bytes)
      const nftMint = new PublicKey(data.slice(offset, offset + 32));
      offset += 32;
      
      // total_votes (u64)
      const totalVotes = data.readBigUInt64LE(offset);
      offset += 8;
      
      // total_vote_sol (u64)
      const totalVoteSol = data.readBigUInt64LE(offset);
      offset += 8;
      
      // total_vote_poppers (u64)
      const totalVotePoppers = data.readBigUInt64LE(offset);
      offset += 8;
      
      // Parse votes_per_round ([u64; 8]) - 64 bytes
      const votesPerRound = [];
      for (let i = 0; i < 8; i++) {
        votesPerRound.push(Number(data.readBigUInt64LE(offset)));
        offset += 8;
      }
      
      // Parse sol_per_round ([u64; 8]) - 64 bytes
      const solPerRound = [];
      for (let i = 0; i < 8; i++) {
        solPerRound.push(Number(data.readBigUInt64LE(offset)));
        offset += 8;
      }
      
      // Parse poppers_per_round ([u64; 8]) - 64 bytes
      const poppersPerRound = [];
      for (let i = 0; i < 8; i++) {
        poppersPerRound.push(Number(data.readBigUInt64LE(offset)));
        offset += 8;
      }
      
      // total_sales (u64)
      const totalSales = data.readBigUInt64LE(offset);
      offset += 8;
      
      // total_royalties_paid (u64)
      const totalRoyaltiesPaid = data.readBigUInt64LE(offset);
      offset += 8;
      
      // created_at (i64)
      const createdAt = Number(data.readBigInt64LE(offset));
      offset += 8;
      
      // created_in_round (u8)
      const createdInRound = data.readUInt8(offset);
      offset += 1;
      
      // last_active_round (u8)
      const lastActiveRound = data.readUInt8(offset);
      offset += 1;
      
      // is_winner (bool)
      const isWinner = data.readUInt8(offset) === 1;
      offset += 1;
      
      // is_archived (bool)
      const isArchived = data.readUInt8(offset) === 1;
      offset += 1;
      
      // is_voting_eligible (bool)
      const isVotingEligible = data.readUInt8(offset) === 1;
      offset += 1;
      
      // product_id (u32)
      const productId = data.readUInt32LE(offset);
      offset += 4;
      
      // bump (u8)
      const bump = data.readUInt8(offset);
      offset += 1;
      
      // reserved space (128 bytes)
      // First byte is length of product image URI, rest is the URI
      // Stats are stored at bytes 101-120
      const reservedStart = offset;
      const productImageUriLength = data.readUInt8(offset);
      let productImageUri = null;
      if (productImageUriLength > 0 && productImageUriLength <= 100) {
        const uriBytes = data.slice(offset + 1, offset + 1 + productImageUriLength);
        productImageUri = uriBytes.toString('utf8');
      }
      // Extract the full reserved bytes for stats reading
      const reserved = Array.from(data.slice(reservedStart, reservedStart + 128));
      offset += 128; // Skip entire reserved space
      
      // Apply filters
      if (votingEligibleOnly && !isVotingEligible) {
        continue;
      }
      
      if (roundNumber !== undefined && createdInRound !== roundNumber) {
        continue;
      }
      
      popperNfts.push({
        pda: pubkey.toBase58(),
        version,
        creatorWallet: creatorWallet.toBase58(),
        currentOwner: currentOwner.toBase58(),
        nftMint: nftMint.toBase58(),
        totalVotes: Number(totalVotes),
        totalVoteSol: Number(totalVoteSol),
        totalVotePoppers: Number(totalVotePoppers),
        votesPerRound,
        solPerRound,
        poppersPerRound,
        totalSales: Number(totalSales),
        totalRoyaltiesPaid: Number(totalRoyaltiesPaid),
        createdAt,
        createdInRound,
        lastActiveRound,
        isWinner,
        isArchived,
        isVotingEligible,
        productId,
        productImageUri,
        reserved, // Include reserved bytes for stats reading
      });
      } catch (deserializeError) {
        console.error(`[fetchAllPopperNFTs] Failed to deserialize account ${pubkey.toBase58()}:`, deserializeError);
        console.error(`[fetchAllPopperNFTs] Account data length: ${account.data.length} bytes`);
        continue;
      }
    }
    
    // Sort by total votes (descending)
    popperNfts.sort((a, b) => b.totalVotes - a.totalVotes);
    
    return popperNfts;
    
  } catch (error) {
    console.error('[fetchAllPopperNFTs] Error:', error);
    return [];
  }
  }); // end deduplicatedFetch
}

/**
 * Fetch NFT metadata from Metaplex
 * 
 * @param {Connection} connection - Solana connection
 * @param {string} mintAddress - NFT mint address
 * @returns {Object|null} NFT metadata (name, symbol, uri, image, etc.)
 */
export async function fetchNFTMetadata(connection, mintAddress) {
  const cacheKey = `metadata_${mintAddress}`;
  
  return deduplicatedFetch(cacheKey, async () => {
  try {
    const mint = new PublicKey(mintAddress);
    
    // Derive metadata PDA
    const [metadataAccount] = await PublicKey.findProgramAddress(
      [
        Buffer.from('metadata'),
        TOKEN_METADATA_PROGRAM_ID.toBuffer(),
        mint.toBuffer(),
      ],
      TOKEN_METADATA_PROGRAM_ID
    );
    
    const accountInfo = await connection.getAccountInfo(metadataAccount);
    if (!accountInfo) {
      console.log(`Metadata not found for mint ${mintAddress}`);
      return null;
    }
    
    const data = accountInfo.data;
    
    // Metaplex Token Metadata v1 layout:
    // - 1 byte: key (enum)
    // - 32 bytes: update_authority
    // - 32 bytes: mint
    // - 4 bytes: name length + 32 bytes: name buffer (FIXED SIZE)
    // - 4 bytes: symbol length + 10 bytes: symbol buffer (FIXED SIZE)
    // - 4 bytes: uri length + 200 bytes: uri buffer (FIXED SIZE)
    
    let offset = 1 + 32 + 32; // Skip key, update_authority, mint
    
    // Read name (4-byte length prefix + 32-byte fixed buffer)
    const nameLen = data.readUInt32LE(offset);
    offset += 4;
    const name = data.slice(offset, offset + Math.min(nameLen, 32)).toString('utf8').replace(/\0/g, '').trim();
    offset += 32; // FIXED: Always skip 32 bytes for name buffer
    
    // Read symbol (4-byte length prefix + 10-byte fixed buffer)
    const symbolLen = data.readUInt32LE(offset);
    offset += 4;
    const symbol = data.slice(offset, offset + Math.min(symbolLen, 10)).toString('utf8').replace(/\0/g, '').trim();
    offset += 10; // FIXED: Always skip 10 bytes for symbol buffer
    
    // Read uri (4-byte length prefix + 200-byte fixed buffer)
    const uriLen = data.readUInt32LE(offset);
    offset += 4;
    const uri = data.slice(offset, offset + Math.min(uriLen, 200)).toString('utf8').replace(/\0/g, '').trim();
    
    // Fetch the actual metadata JSON from the URI
    let image = null;
    let description = null;
    
    if (uri && uri.startsWith('http')) {
      try {
        const response = await fetch(uri);
        if (response.ok) {
          const metadata = await response.json();
          image = metadata.image;
          description = metadata.description;
        }
      } catch (fetchError) {
        console.warn(`Failed to fetch metadata JSON from ${uri}:`, fetchError);
      }
    }
    
    const result = { name, symbol, uri, image, description };
    return result;
    
  } catch (error) {
    console.error(`[fetchNFTMetadata] Error for mint ${mintAddress}:`, error);
    return null;
  }
  }); // end deduplicatedFetch
}
